import { useEffect, useState } from 'react';
import { Wallet, Save, Loader2, Eye, Building2, Plus, Trash2, QrCode } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

interface Instituicao {
  id: number;
  padrao: string;
  nome_exibicao: string;
  ordem: number;
  ativo: boolean;
}

interface CaixaConta {
  id: number;
  nome: string;
  tipo: string;
}

// Defaults espelham o backend (monitoramento-recebimentos.ts).
const DEF_SEGUNDOS = '60';
const DEF_INICIO = '07:00';
const DEF_FIM = '20:00';
const DEF_DIAS = 'seg_sab';

export function Recebimentos() {
  const toast = useToast();
  const [caixas, setCaixas] = useState<CaixaConta[]>([]);
  const [caixaDefaultId, setCaixaDefaultId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Vigia de recebimentos (intervalo + janela de horario) — vale pro Mercado Pago e futuras contas.
  const [segundos, setSegundos] = useState(DEF_SEGUNDOS);
  const [inicio, setInicio] = useState(DEF_INICIO);
  const [fim, setFim] = useState(DEF_FIM);
  const [dias, setDias] = useState(DEF_DIAS);
  const [salvandoVigia, setSalvandoVigia] = useState(false);

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      setLoading(true);
      try {
        const ler = (chave: string) => api.get<{ valor: string | null }>(`/api/app-settings/${chave}`).catch(() => ({ valor: null }));
        const [lista, cfg, seg, ini, f, d] = await Promise.all([
          api.get<CaixaConta[]>('/api/caixa/contas'),
          ler('caixa_destino_default'),
          ler('recebimentos_poll_segundos'),
          ler('recebimentos_monitor_inicio'),
          ler('recebimentos_monitor_fim'),
          ler('recebimentos_monitor_dias'),
        ]);
        if (cancelado) return;
        setCaixas(lista);
        const idGravado = Number(cfg?.valor);
        if (idGravado && lista.some((c) => c.id === idGravado)) {
          setCaixaDefaultId(idGravado);
        }
        if (seg?.valor) setSegundos(seg.valor);
        if (ini?.valor) setInicio(ini.valor);
        if (f?.valor) setFim(f.valor);
        if (d?.valor) setDias(d.valor);
      } catch (e: any) {
        toast.erro(e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    void carregar();
    return () => { cancelado = true; };
  }, [toast]);

  async function salvar() {
    setSalvando(true);
    try {
      await api.put('/api/app-settings/caixa_destino_default', { valor: caixaDefaultId === '' ? '' : String(caixaDefaultId) });
      toast.sucesso('Configuracao salva');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarVigia() {
    const seg = Number(segundos);
    if (!Number.isFinite(seg) || seg < 15 || seg > 600) {
      toast.erro('Intervalo deve ficar entre 15 e 600 segundos');
      return;
    }
    if (!/^\d{1,2}:\d{2}$/.test(inicio) || !/^\d{1,2}:\d{2}$/.test(fim)) {
      toast.erro('Horario invalido');
      return;
    }
    setSalvandoVigia(true);
    try {
      await Promise.all([
        api.put('/api/app-settings/recebimentos_poll_segundos', { valor: String(Math.round(seg)) }),
        api.put('/api/app-settings/recebimentos_monitor_inicio', { valor: inicio }),
        api.put('/api/app-settings/recebimentos_monitor_fim', { valor: fim }),
        api.put('/api/app-settings/recebimentos_monitor_dias', { valor: dias }),
      ]);
      toast.sucesso('Vigia de recebimentos atualizado');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvandoVigia(false);
    }
  }

  const labelInput = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 } as const;
  const campo = { padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13, background: 'var(--input-bg, #fff)' } as const;

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pagina-titulo">
          <Wallet size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Recebimentos
        </h2>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> Carregando...
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelInput}>Caixa destino (Default)</label>
              <select
                value={caixaDefaultId}
                onChange={(e) => setCaixaDefaultId(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ ...campo, width: '100%' }}
              >
                <option value="">— Sem default (usa o primeiro caixa que aceita a forma) —</option>
                {caixas.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Este caixa vira pre-selecionado na tela <b>Finalizar Venda</b> sempre que a forma de pagamento aceitar — para PIX, Dinheiro, Cartao etc.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-primario"
                onClick={() => void salvar()}
                disabled={salvando}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvar
              </button>
            </div>
          </div>

          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Eye size={16} /> Vigia de recebimentos
            </h3>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
              De quanto em quanto tempo e em que horario o sistema verifica sozinho se caiu Pix na conta
              (avisa com faixa no topo + som em qualquer tela). Vale pro <b>Mercado Pago</b> e pras proximas
              contas (InfinitePay, Pagbank, Cora). Fora do horario, nem consulta — economiza recurso.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelInput}>Verificar a cada (segundos)</label>
                <input
                  type="number" min={15} max={600} step={5}
                  value={segundos}
                  onChange={(e) => setSegundos(e.target.value)}
                  style={{ ...campo, width: '100%' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Entre 15 e 600. Padrao 60.</div>
              </div>
              <div>
                <label style={labelInput}>Dias</label>
                <select value={dias} onChange={(e) => setDias(e.target.value)} style={{ ...campo, width: '100%' }}>
                  <option value="seg_sab">Segunda a sabado</option>
                  <option value="seg_sex">Segunda a sexta</option>
                  <option value="todos">Todos os dias</option>
                </select>
              </div>
              <div>
                <label style={labelInput}>Comeca as</label>
                <input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)} style={{ ...campo, width: '100%' }} />
              </div>
              <div>
                <label style={labelInput}>Para as</label>
                <input type="time" value={fim} onChange={(e) => setFim(e.target.value)} style={{ ...campo, width: '100%' }} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-primario"
                onClick={() => void salvarVigia()}
                disabled={salvandoVigia}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {salvandoVigia ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvar vigia
              </button>
            </div>
          </div>

          <PixDaLoja />

          <TraducaoInstituicoes />
        </div>
      )}
    </div>
  );
}

// Chave Pix da loja usada pelo botao "Pix" do Atendimento (envia QR + chave pro contato).
// Default = CNPJ do emitente fiscal; aqui o admin troca a chave/conta sem mexer no codigo.
// Banco/agencia/conta (29/09/2026) entram na legenda do QR; a chave vai tambem numa 2a
// mensagem sozinha, pra copiar. A previa mostra as DUAS como o contato recebe.
interface PixPreview {
  chave: string; banco: string; agencia: string; conta: string;
  rotulo: string; recebedor: string; legenda: string; chave_copiavel: string; qr_base64: string;
}
type CamposPix = { chave: string; banco: string; agencia: string; conta: string };
function PixDaLoja() {
  const toast = useToast();
  const [campos, setCampos] = useState<CamposPix>({ chave: '', banco: '', agencia: '', conta: '' });
  const [preview, setPreview] = useState<PixPreview | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Sem argumento: le o que esta salvo e preenche os campos. Com argumento: previa do
  // que esta digitado, sem salvar.
  async function carregarPreview(teste?: CamposPix) {
    try {
      const q = teste
        ? '?' + new URLSearchParams({
            ...(teste.chave.trim().length >= 3 ? { chave: teste.chave.trim() } : {}),
            banco: teste.banco.trim(), agencia: teste.agencia.trim(), conta: teste.conta.trim(),
          }).toString()
        : '';
      const r = await api.get<PixPreview>(`/api/pix/preview${q}`);
      setPreview(r);
      if (!teste) setCampos({ chave: r.chave, banco: r.banco, agencia: r.agencia, conta: r.conta });
    } catch (e: any) {
      toast.erro(e.message || 'Nao foi possivel gerar o QR para esta chave');
    }
  }

  useEffect(() => {
    let cancelado = false;
    (async () => { if (!cancelado) await carregarPreview(); setCarregando(false); })();
    return () => { cancelado = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function salvar() {
    if (campos.chave.trim().length < 3) { toast.erro('Informe a chave Pix'); return; }
    setSalvando(true);
    try {
      await api.put('/api/app-settings/pix_chave', { valor: campos.chave.trim() });
      await api.put('/api/app-settings/pix_banco', { valor: campos.banco.trim().toUpperCase() });
      await api.put('/api/app-settings/pix_agencia', { valor: campos.agencia.trim() });
      await api.put('/api/app-settings/pix_conta', { valor: campos.conta.trim() });
      await carregarPreview();
      toast.sucesso('Pix da loja salvo');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const campo = { padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13, background: 'var(--input-bg, #fff)' } as const;
  const rotuloCampo = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 } as const;
  // Balao com par FIXO (fundo branco + letra preta): variavel de tema nos dois lados nao
  // garante contraste entre eles.
  const balao = { background: '#fff', color: '#000', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.45 } as const;

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <QrCode size={16} /> Pix da loja (QR do Atendimento)
      </h3>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        Chave e conta usadas pelo botao <b>Pix</b> do chat de Atendimento — manda o QR Code com os dados da conta e, em seguida, a chave sozinha pro contato copiar.
        Gera o QR oficial do Banco Central a partir desta chave (CNPJ, telefone, e-mail ou aleatoria); nao depende
        do Mercado Pago. <b>Escaneie o QR abaixo</b> com o app do banco pra conferir que cai na conta certa.
      </div>

      {carregando ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}><Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> Carregando…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
          <div>
            <label style={rotuloCampo}>Chave Pix</label>
            <input
              value={campos.chave}
              onChange={(e) => setCampos({ ...campos, chave: e.target.value })}
              onBlur={() => { if (campos.chave.trim().length >= 3) void carregarPreview(campos); }}
              placeholder="CNPJ, telefone, e-mail ou chave aleatoria"
              style={{ ...campo, width: '100%' }}
            />
            <label style={{ ...rotuloCampo, marginTop: 10 }}>Banco</label>
            <input
              value={campos.banco}
              onChange={(e) => setCampos({ ...campos, banco: e.target.value.toUpperCase() })}
              onBlur={() => void carregarPreview(campos)}
              placeholder="Nome e codigo do banco"
              style={{ ...campo, width: '100%' }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.6fr)', gap: 8, marginTop: 10 }}>
              <div>
                <label style={rotuloCampo}>Agência</label>
                <input
                  value={campos.agencia}
                  onChange={(e) => setCampos({ ...campos, agencia: e.target.value })}
                  onBlur={() => void carregarPreview(campos)}
                  style={{ ...campo, width: '100%' }}
                />
              </div>
              <div>
                <label style={rotuloCampo}>Conta</label>
                <input
                  value={campos.conta}
                  onChange={(e) => setCampos({ ...campos, conta: e.target.value })}
                  onBlur={() => void carregarPreview(campos)}
                  style={{ ...campo, width: '100%' }}
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Campo deixado em branco nao aparece na mensagem.
            </div>
            {preview && (
              <div style={{ marginTop: 12 }}>
                <div style={rotuloCampo}>O contato recebe:</div>
                <div style={balao}>
                  {preview.legenda.split('\n').map((linha, i) =>
                    linha.startsWith('🔑') ? <div key={i}><b>{linha.replace(/\*/g, '')}</b></div> : <div key={i}>{linha || ' '}</div>,
                  )}
                </div>
                <div style={{ ...balao, marginTop: 6, display: 'inline-block' }}>{preview.chave_copiavel}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  A 2a mensagem vai sozinha, so com a chave, pro cliente copiar com um toque.
                </div>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn-primario"
                onClick={() => void salvar()}
                disabled={salvando}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvar Pix
              </button>
            </div>
          </div>
          {preview?.qr_base64 && (
            <div style={{ textAlign: 'center' }}>
              <img
                src={`data:image/png;base64,${preview.qr_base64}`}
                alt="QR Code Pix da loja"
                style={{ width: 160, height: 160, border: '1px solid var(--border)', borderRadius: 8, background: '#fff', padding: 6 }}
              />
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 4 }}>Pré-visualização</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Cadastro da tradução de nomes de instituição (CLOUDWALK → INFINITEPAY). O que não estiver
// aqui aparece com o nome original. Casa por trecho contido, ignorando acento e maiúsc/minúsc.
function TraducaoInstituicoes() {
  const toast = useToast();
  const [lista, setLista] = useState<Instituicao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [novoPadrao, setNovoPadrao] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [salvandoNovo, setSalvandoNovo] = useState(false);

  async function carregar() {
    try {
      const r = await api.get<Instituicao[]>('/api/configuracoes/instituicoes');
      setLista(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }
  useEffect(() => { void carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function adicionar() {
    if (!novoPadrao.trim() || !novoNome.trim()) { toast.erro('Preencha o nome original e o nome de exibição'); return; }
    setSalvandoNovo(true);
    try {
      await api.post('/api/configuracoes/instituicoes', { padrao: novoPadrao.trim(), nome_exibicao: novoNome.trim() });
      setNovoPadrao(''); setNovoNome('');
      await carregar();
      toast.sucesso('Instituição adicionada');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvandoNovo(false);
    }
  }

  async function salvarLinha(inst: Instituicao) {
    try {
      await api.put(`/api/configuracoes/instituicoes/${inst.id}`, inst);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  async function remover(id: number) {
    try {
      await api.delete(`/api/configuracoes/instituicoes/${id}`);
      setLista((l) => l.filter((x) => x.id !== id));
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const campo = { padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13, background: 'var(--input-bg, #fff)' } as const;

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Building2 size={16} /> Tradução de instituições
      </h3>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        O Pix mostra o nome jurídico da origem (ex.: <b>CLOUDWALK INSTITUIÇÃO DE PAGAMENTO…</b>). Aqui você
        traduz pro nome comercial (<b>INFINITEPAY</b>). Casa por <b>trecho contido</b> (ignora acento e
        maiúsc/minúsc). O que não estiver cadastrado aparece com o nome original.
      </div>

      {carregando ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}><Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> Carregando…</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 56px 36px', gap: 8, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
            <span>Nome original (trecho)</span><span>Exibir como</span><span>Ativo</span><span />
          </div>
          {lista.map((inst) => (
            <div key={inst.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 56px 36px', gap: 8, alignItems: 'center' }}>
              <input
                value={inst.padrao}
                onChange={(e) => setLista((l) => l.map((x) => x.id === inst.id ? { ...x, padrao: e.target.value } : x))}
                onBlur={() => void salvarLinha(inst)}
                style={{ ...campo, width: '100%' }}
              />
              <input
                value={inst.nome_exibicao}
                onChange={(e) => setLista((l) => l.map((x) => x.id === inst.id ? { ...x, nome_exibicao: e.target.value } : x))}
                onBlur={() => void salvarLinha(inst)}
                style={{ ...campo, width: '100%' }}
              />
              <input
                type="checkbox"
                checked={inst.ativo}
                onChange={(e) => {
                  const novo = { ...inst, ativo: e.target.checked };
                  setLista((l) => l.map((x) => x.id === inst.id ? novo : x));
                  void salvarLinha(novo);
                }}
                style={{ width: 18, height: 18 }}
              />
              <button type="button" onClick={() => void remover(inst.id)} title="Remover" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 56px 36px', gap: 8, alignItems: 'center', marginTop: 6, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
            <input placeholder="ex.: CLOUDWALK" value={novoPadrao} onChange={(e) => setNovoPadrao(e.target.value)} style={{ ...campo, width: '100%' }} />
            <input placeholder="ex.: INFINITEPAY" value={novoNome} onChange={(e) => setNovoNome(e.target.value)} style={{ ...campo, width: '100%' }} />
            <button type="button" onClick={() => void adicionar()} disabled={salvandoNovo} title="Adicionar" style={{ gridColumn: '3 / span 2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, background: '#2563eb', color: '#fff', border: 0, borderRadius: 4, padding: '6px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              {salvandoNovo ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
